import { Box } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { showV2CompactToast, showV2Toast } from "#app/notifications";
import { Button } from "#components/atoms/Button/Button";
import { BulkActionBar } from "#components/molecules/BulkActionBar/BulkActionBar";
import { BulkActionBarOverlay } from "#components/molecules/BulkActionBar/BulkActionBarOverlay";
import { Table, type TableHandle } from "#components/organisms/Table/Table";
import { useCompany } from "#modules/company/view/useCompany";
import {
  getMaterialsVolumetricPriceQueryData,
  setMaterialsVolumetricPriceQueryData,
} from "#modules/materials/infra/materialsVolumetricPrice.query";
import { useBulkDeleteMaterialVolumetricPrices } from "#modules/materials/view/useBulkDeleteMaterialVolumetricPrices";
import { useDeleteMaterialVolumetricPrice } from "#modules/materials/view/useDeleteMaterialVolumetricPrice";
import { useMaterialsVolumetricPrice } from "#modules/materials/view/useMaterialsVolumetricPrice";
import { useUpdateMaterialsVolumetricPrice } from "#modules/materials/view/useUpdateMaterialsVolumetricPrice";
import { MaterialSpecificationEmptyState } from "#modules/onboardingV2/view/materialSpecification/MaterialSpecificationEmptyState";
import { useDeletableMaterialRows } from "#modules/onboardingV2/view/materialSpecification/useDeletableMaterialRows";
import { OnboardingV2Shell } from "#modules/onboardingV2/view/OnboardingV2Shell";
import { useAddDefaultOnboardingProcessSteps } from "#modules/onboardingV2/view/processStepConfiguration/useAddDefaultOnboardingProcessSteps";
import { useOnboardingActivityTypeLabel } from "#modules/onboardingV2/view/useOnboardingActivityTypeLabel";
import {
  useOnboardingAnalytics,
  useTrackOnboardingStepViewed,
} from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { useOnboardingStepNavigation } from "#modules/onboardingV2/view/useOnboardingStepNavigation";
import { useUndoableBulkDelete } from "#modules/onboardingV2/view/useUndoableBulkDelete";
import { AddVolumetricMaterialModal } from "#modules/onboardingV2/view/volumetricMaterialSpecification/AddVolumetricMaterialModal";
import {
  type MaterialRow,
  type MaterialRowChanges,
  useVolumetricMaterialSpecificationColumns,
} from "#modules/onboardingV2/view/volumetricMaterialSpecification/useVolumetricMaterialSpecificationColumns";
import { Icon } from "#shared/icons/Icon";
import { AnalyticsEvent } from "#shared/useAnalytics";
import {
  findDuplicateVolumetricMaterialIds,
  MaterialVolumetricPricingStrategySchema,
} from "shared";

export const OnboardingVolumetricMaterialSpecificationStepPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { company } = useCompany();
  const { position, total, goToNext, goToPrevious } =
    useOnboardingStepNavigation("materialSpecification");
  const { materialsVolumetricPrice } = useMaterialsVolumetricPrice();
  const { updateMaterialsVolumetricPrice } = useUpdateMaterialsVolumetricPrice();
  const { deleteMaterialVolumetricPrice } = useDeleteMaterialVolumetricPrice();
  const { bulkDeleteMaterialVolumetricPrices } = useBulkDeleteMaterialVolumetricPrices();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasCheckedDuplicates, setHasCheckedDuplicates] = useState(false);
  const tableRef = useRef<TableHandle>(null);
  const [isAddModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);

  useTrackOnboardingStepViewed("materialSpecification");
  const { captureOnboardingEvent } = useOnboardingAnalytics("materialSpecification");

  const handleOpenAddModal = () => {
    captureOnboardingEvent(AnalyticsEvent.OnboardingAddMaterialClicked);
    openAddModal();
  };

  const priceColumnLabel =
    company?.companySettings.materialVolumetricPricingStrategy ===
    MaterialVolumetricPricingStrategySchema.enum.CAPABLE_RECTANGLE
      ? t("onboardingV2.materialSpecification.priceColumn.capableRectangle")
      : t("onboardingV2.materialSpecification.priceColumn.realWeight");

  const activityType = useOnboardingActivityTypeLabel();

  const { visibleRows, deleteRow, commitPendingRowDeletes } = useDeletableMaterialRows<MaterialRow>(
    {
      rows: materialsVolumetricPrice ?? [],
      getRowId: (row) => row.id,
      getDeleteToastMessage: (count) =>
        t("onboardingV2.materialSpecification.deleteToast.message", { count }),
      getUndoToastMessage: (count) =>
        t("onboardingV2.materialSpecification.undoToast.message", { count }),
      onConfirmDelete: (row) => deleteMaterialVolumetricPrice(row.id),
      onConfirmBulkDelete: (rowsToDelete) =>
        bulkDeleteMaterialVolumetricPrices({
          ids: rowsToDelete.map((row) => row.id),
        }),
    },
  );

  const handleClearSelection = () => {
    tableRef.current?.resetRowSelection();
    setSelectedIds([]);
  };

  const { bulkDelete, commitPendingBulkDelete } = useUndoableBulkDelete({
    getRowId: (row: MaterialRow) => row.id,
    getData: () => getMaterialsVolumetricPriceQueryData(queryClient),
    setData: (rows) => setMaterialsVolumetricPriceQueryData(queryClient, rows),
    onCommit: (ids) => bulkDeleteMaterialVolumetricPrices({ ids }),
    showUndoToast: ({ id, count, onCancel }) =>
      showV2Toast({
        id,
        message: t("onboardingV2.materialSpecification.bulkDelete.toast", {
          count,
        }),
        onCancel,
      }),
    onUndo: () => {
      handleClearSelection();
      showV2CompactToast({
        message: t("onboardingV2.materialSpecification.bulkDelete.undoToast"),
      });
    },
  });

  const { addDefaultOnboardingProcessSteps, isPending: isAddingDefaultProcessSteps } =
    useAddDefaultOnboardingProcessSteps();

  const handleBulkDelete = () => {
    bulkDelete(selectedIds);
    setSelectedIds([]);
  };

  const latestRowValuesRef = useRef<Record<string, MaterialRow>>({});

  useEffect(() => {
    materialsVolumetricPrice?.forEach((row) => {
      latestRowValuesRef.current[row.id] = row;
    });
  }, [materialsVolumetricPrice]);

  const duplicateIds = useMemo(
    () =>
      hasCheckedDuplicates
        ? new Set(findDuplicateVolumetricMaterialIds(visibleRows))
        : new Set<string>(),
    [hasCheckedDuplicates, visibleRows],
  );

  const commitRowChange = (row: MaterialRow, changes: MaterialRowChanges) => {
    const mergedRow = {
      ...(latestRowValuesRef.current[row.id] ?? row),
      ...changes,
    };
    latestRowValuesRef.current[row.id] = mergedRow;

    updateMaterialsVolumetricPrice({
      id: mergedRow.id,
      materialGrade: mergedRow.materialGrade,
      name: mergedRow.name,
      volumetricPrice: mergedRow.volumetricPrice,
    });
  };

  const columns = useVolumetricMaterialSpecificationColumns(
    priceColumnLabel,
    commitRowChange,
    deleteRow,
  );

  const handleContinue = async () => {
    if (isAddingDefaultProcessSteps) {
      return;
    }

    if (!hasCheckedDuplicates) {
      if (findDuplicateVolumetricMaterialIds(visibleRows).length > 0) {
        setHasCheckedDuplicates(true);
        return;
      }
    } else if (duplicateIds.size > 0) {
      return;
    }

    try {
      await addDefaultOnboardingProcessSteps();
    } catch {
      return;
    }

    commitPendingBulkDelete();
    commitPendingRowDeletes();
    goToNext();
  };

  return (
    <OnboardingV2Shell size="lg">
      <OnboardingV2Shell.Header
        onBack={goToPrevious}
        step={position}
        total={total}
        title={t("onboardingV2.materialSpecification.title")}
        subtitle={t("onboardingV2.rawMaterialSpecification.subtitle", {
          activityType,
        })}
      />

      <Box
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <OnboardingV2Shell.Content>
          <Table
            ref={tableRef}
            data={visibleRows}
            columns={columns}
            getRowId={(row) => row.id}
            getRowError={(row) => duplicateIds.has(row.id)}
            emptyState={<MaterialSpecificationEmptyState onAddMaterial={handleOpenAddModal} />}
            onRowSelectionChange={setSelectedIds}
          />
        </OnboardingV2Shell.Content>
        <BulkActionBarOverlay visible={selectedIds.length > 0}>
          <BulkActionBar
            label={t("onboardingV2.materialSpecification.bulkDelete.selectedCount", {
              count: selectedIds.length,
            })}
            clearAriaLabel={t("onboardingV2.materialSpecification.bulkDelete.clearSelection")}
            onClear={handleClearSelection}
            actions={[
              {
                key: "delete",
                icon: <Icon name="trash" size={16} />,
                label: t("common.delete"),
                onClick: handleBulkDelete,
              },
            ]}
          />
        </BulkActionBarOverlay>
      </Box>

      <OnboardingV2Shell.Footer
        buttonLabel={t("onboardingV2.continue")}
        onContinue={handleContinue}
        loading={isAddingDefaultProcessSteps}
        errorMessage={
          hasCheckedDuplicates && duplicateIds.size > 0
            ? t("onboardingV2.duplicateMaterialsError")
            : undefined
        }
        stepper={{ total, current: position }}
        leftAction={
          <Button
            variant="secondary"
            size="md"
            leftSection={<Icon name="plus" size={16} />}
            onClick={handleOpenAddModal}
          >
            {t("onboardingV2.rawMaterialSpecification.addMaterial")}
          </Button>
        }
      />

      <AddVolumetricMaterialModal opened={isAddModalOpened} onClose={closeAddModal} />
    </OnboardingV2Shell>
  );
};
